import { useNavigate } from 'react-router-dom';

import { ArrowRightCircleIcon, Plus } from 'lucide-react';

import { useFeatureFlags, useLanguage } from '../../../hooks';
import { useUserDataStore } from '../../../newStore/userDataStore';
import { datascopeTracker } from '../../../utils/eventTracker';
import { paths } from '../../../utils/navigation';
import { Button } from '../../shadcn-ui/button';
import { GrayTooltip } from '../../shadcn-ui/custom/GrayTooltip';
import { useNestedLocationsContext } from '../contexts/NestedLocationsContext';

interface LocationsCreateButtonProps {
  variant?:
    | 'primary'
    | 'taskAssignCreation'
    | 'emptyStateLocationsSelector'
    | 'emptyStateLocationsSelector2';
}

export function LocationsCreateButton({ variant = 'primary' }: LocationsCreateButtonProps) {
  const lang = useLanguage();
  const navigate = useNavigate();
  const { currentUser } = useUserDataStore();
  const { checkFlag } = useFeatureFlags();
  const hasAdminPermissions = useUserDataStore((state) => state.hasAdminRoleOnLocations());
  const { numberOfNestableLocations, maxNumberOfLocations } = useNestedLocationsContext();

  const sourceMap: Record<LocationsCreateButtonProps['variant'], string> = {
    primary: 'locations_index',
    taskAssignCreation: 'task_assigner',
    emptyStateLocationsSelector: 'locations_selector',
    emptyStateLocationsSelector2: 'locations_selector',
  };
  const source = sourceMap[variant];

  const handleCreateLocation = () => {
    datascopeTracker('new_location', { source, accountId: currentUser?.id }, 'AMP');
    if (variant === 'taskAssignCreation') {
      if (checkFlag('NestableLocations')) {
        window.open(`${window.location.origin}${paths.locations.createRoot}`, '_blank');
      } else {
        window.open(`${window.location.origin}${paths.lists.newLocation}`, '_blank');
      }
    } else if (variant.startsWith('emptyStateLocationsSelector')) {
      window.open(`${window.location.origin}${paths.locations.createRoot}`, '_blank');
    } else {
      navigate(paths.locations.createRoot);
    }
  };

  let hasReachedMaxNumberOfLocations = maxNumberOfLocations ? numberOfNestableLocations >= maxNumberOfLocations : false;
  const tooltipText = hasReachedMaxNumberOfLocations ? (
    <span>
      {lang.locations.planLimits.actionBlocked1}
      <br />
      {lang.locations.planLimits.actionBlocked2}
    </span>
  ) : null;

  let button = null;
  if (!hasAdminPermissions) {
    return button;
  }

  if (variant === 'emptyStateLocationsSelector') {
    button = (
      <Button
        onClick={handleCreateLocation}
        disabled={hasReachedMaxNumberOfLocations}
      >
        {lang.locations.emptyState.createButton} <ArrowRightCircleIcon />
      </Button>
    );
  }

  if (variant === 'emptyStateLocationsSelector2') {
    const isDisabled = hasReachedMaxNumberOfLocations;
    const hiddenClass = isDisabled ? 'hidden' : '';
    button = (
      <div
        onClick={handleCreateLocation}
        className={`text-[16px] text-[#3E8FFA] cursor-pointer hover:underline flex items-center gap-1 ${hiddenClass}`}
      >
        <Plus size={16} />
        <span>{lang.locations.emptyState.createNewLocation}</span>
      </div>
    );
  }

  if (variant === 'taskAssignCreation') {
    hasReachedMaxNumberOfLocations = hasReachedMaxNumberOfLocations && checkFlag('NestableLocations');
    button = (
      <Button
        id='new-location'
        onClick={handleCreateLocation}
        disabled={hasReachedMaxNumberOfLocations}
        variant='ghost'
        className='text-primary color-primary hover:bg-blue-50 hover:text-primary mt-[5px]'
      >
        <Plus className='h-5 w-5 ml-1' />
        {lang.locations.header.createPlace}
      </Button>
    );
  }

  if (!button) {
    button = (
      <Button
        id='new-location'
        onClick={handleCreateLocation}
        disabled={hasReachedMaxNumberOfLocations}
        size='sm'
      >
        {lang.locations.header.createPlace}
        <Plus className='h-5 w-5 ml-1' />
      </Button>
    );
  }

  return (
    <GrayTooltip
      message={tooltipText}
      hidden={!hasReachedMaxNumberOfLocations}
    >
      {button}
    </GrayTooltip>
  );
}
